
import { supabase } from '@/integrations/supabase/client';
import { Procurement } from '@/types/database.types';

/**
 * Helper function to get requestor data separately
 */
export const getRequestorData = async (procurement: any) => {
  if (!procurement.requestor_id) return null;
  
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', procurement.requestor_id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching requestor data:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getRequestorData:', error);
    return null;
  }
};

/**
 * Transform and enrich procurement data from database queries
 */
export const enrichProcurementData = async (
  procurements: any[], 
  sourceNames: Record<string, string | null>
): Promise<Procurement[]> => {
  console.log('Starting enrichProcurementData with:', procurements.length, 'procurements');
  
  return procurements.map((procurement) => {
    console.log(`Processing procurement: ${procurement.id}`);
    console.log('Raw finance data:', procurement.finance);
    console.log('Raw requestor data before processing:', procurement.requestor);
    console.log('Requestor ID:', procurement.requestor_id);
    
    // Extract and preserve all finance data
    const financeData = procurement.finance ? {
      ...procurement.finance,
      // Ensure boolean fields are properly handled
      has_advance_payment: Boolean(procurement.finance.has_advance_payment),
      payment_confirmed: Boolean(procurement.finance.payment_confirmed),
      supports_damage_return: Boolean(procurement.finance.supports_damage_return),
      advance_payment_confirmed: Boolean(procurement.finance.advance_payment_confirmed),
      remaining_payment_confirmed: Boolean(procurement.finance.remaining_payment_confirmed),
      // Handle user profiles for approved_by and rejected_by
      approved_by: procurement.finance.approved_by_profile || procurement.finance.approved_by,
      rejected_by: procurement.finance.rejected_by_profile || procurement.finance.rejected_by
    } : null;

    console.log('Processed finance data:', financeData);

    // Extract requestor data - PRESERVE manually attached requestor data
    let requestorData = null;
    
    // Check if requestor data was already manually attached (finance route)
    if (procurement.requestor && typeof procurement.requestor === 'object') {
      console.log('Using manually attached requestor data from finance service');
      requestorData = {
        id: procurement.requestor.id,
        first_name: procurement.requestor.first_name,
        last_name: procurement.requestor.last_name,
        email: procurement.requestor.email,
        role: procurement.requestor.role,
        created_at: procurement.requestor.created_at,
        updated_at: procurement.requestor.updated_at,
        status: procurement.requestor.status || 'active'
      };
    } else if (procurement.requestor_id) {
      // If no requestor data but we have requestor_id, this might be admin route
      console.log('No manual requestor data found, requestor_id exists but no requestor object');
      requestorData = null; // Will be handled separately in admin flows
    }

    console.log('Final requestor data after processing:', requestorData);

    // Extract warehouse data
    const warehouseData = procurement.warehouse ? {
      ...procurement.warehouse
    } : null;

    // Build the enriched procurement object
    const enrichedProcurement: Procurement = {
      ...procurement,
      source_name: sourceNames[procurement.id] || null,
      requestor: requestorData,
      finance: financeData,
      warehouse: warehouseData,
      items: procurement.items || [],
      costs: procurement.costs || null,
      audit_trail: procurement.audit_trail || []
    };

    console.log(`Processed procurement: ${procurement.id} - Requestor exists:`, !!enrichedProcurement.requestor);
    if (enrichedProcurement.requestor) {
      console.log('Final requestor name:', `${enrichedProcurement.requestor.first_name} ${enrichedProcurement.requestor.last_name}`);
    }
    if (enrichedProcurement.finance) {
      console.log('Final finance has_advance_payment:', enrichedProcurement.finance.has_advance_payment);
    }

    return enrichedProcurement;
  });
};
